import React, { useState } from "react";
import {
  Building,
  Hash,
  Package,
  Phone,
  TrendingUp,
  User,
  X,
  Zap,
  CreditCard,
  Calendar,
  Calculator,
  Receipt,
  Settings,
  PackagePlus,
  CircleDollarSign,
} from "lucide-react";
import { IEpcQuotation } from "../../models/epcCustomer.model";

interface QuotationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation?: IEpcQuotation;
}

const QuotationDetailsModal: React.FC<QuotationDetailsModalProps> = ({
  isOpen,
  onClose,
  quotation,
}) => {
  const [activeTab, setActiveTab] = useState<"overview" | "system" | "pricing">(
    "overview",
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const subTotal = quotation?.totalCost || 0;

  if (!isOpen || !quotation) return null;

  const tabs = [
    { id: "overview", label: "Overview", icon: User },
    { id: "system", label: "System Details", icon: Settings },
    { id: "pricing", label: "Pricing", icon: CreditCard },
  ];

  return (
    <div className="fixed inset-0 z-50">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-semibold text-lg">
                {quotation?.customerName?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {quotation?.customerName}
                </h2>
                <p className="text-sm text-gray-500 flex items-center mt-1">
                  <Phone className="w-4 h-4 mr-1" />
                  {quotation?.mobileNumber}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id as any)}
                    className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab?.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {activeTab === "overview" && (
              <div className="space-y-6">
                {/* Customer Information */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Customer Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Full Name</p>
                        <p className="font-medium text-gray-900">
                          {quotation?.customerName}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Mobile Number</p>
                        <p className="font-medium text-gray-900 font-mono">
                          {quotation?.mobileNumber}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Building className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          Sales Representative
                        </p>
                        <p className="font-medium text-gray-900">
                          {quotation?.salesRepresentative?.name ||
                            quotation?.cseID?.username}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Created Date</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(quotation?.createdAt || "")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "system" && (
              <div className="space-y-8">
                {/* System Specifications */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-slate-900 mb-6 tracking-tight">
                    System Specifications
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg shadow-sm flex items-center justify-center">
                        <Package className="w-6 h-6 text-slate-700" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                          Brand
                        </p>
                        <p className="text-lg font-semibold text-slate-900">
                          {quotation?.brand}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg shadow-sm flex items-center justify-center">
                        <Zap className="w-6 h-6 text-slate-700" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                          Panel Type
                        </p>
                        <p className="text-lg font-semibold text-slate-900">
                          {quotation?.panelType}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg shadow-sm flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-slate-700" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                          Plant Size
                        </p>
                        <p className="text-lg font-semibold text-slate-900">
                          {quotation?.plantSize}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Technical Information */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-slate-900 mb-6 tracking-tight">
                    Technical Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Hash className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                          HSN Code
                        </p>
                        <p className="font-semibold text-slate-900 font-mono">
                          {quotation?.hsn}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "pricing" && (
              <div className="space-y-4">
                {/* Cost Breakdown */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                    <Receipt className="w-5 h-5 mr-2 text-slate-600" />
                    Cost Breakdown
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <div className="flex items-center space-x-2">
                        <Package className="w-4 h-4 text-slate-600" />
                        <span className="text-slate-700 text-sm font-medium">
                          Solar Plant Installation
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-slate-900 font-mono">
                        {formatCurrency(quotation?.plantCost)}
                      </span>
                    </div>

                    {typeof quotation?.meterCost === "number" &&
                      quotation?.meterCost > 0 && (
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <div className="flex items-center space-x-2">
                            <Zap className="w-4 h-4 text-slate-600" />
                            <span className="text-slate-700 text-sm font-medium">
                              Net Metering Setup
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-slate-900 font-mono">
                            {formatCurrency(quotation?.meterCost)}
                          </span>
                        </div>
                      )}

                    {typeof quotation?.discount === "number" &&
                      quotation?.discount > 0 && (
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <div className="flex items-center space-x-2">
                            <CircleDollarSign className="w-4 h-4 text-red-700" />
                            <span className="text-red-700 text-sm font-medium">
                              Discount
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-red-700 font-mono">
                            {formatCurrency(quotation?.discount)}
                          </span>
                        </div>
                      )}

                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <div className="flex items-center space-x-2">
                        <Calculator className="w-4 h-4 text-slate-600" />
                        <span className="text-slate-700 text-sm font-medium">
                          Subtotal (Before Tax)
                        </span>
                      </div>
                      <div className="space-y-1.5 text-right">
                        {/* Final Subtotal */}
                        <div className="flex justify-end items-center">
                          <span className="text-sm font-bold text-slate-900 font-mono">
                            {formatCurrency(subTotal)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {quotation?.excessAmount && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <div className="flex items-center space-x-2">
                          <PackagePlus className="w-4 h-4 text-slate-600" />
                          <span className="text-slate-700 text-sm font-medium">
                            Excess Amount
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-slate-900 font-mono">
                          {formatCurrency(quotation?.excessAmount)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Final Total */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-5 border border-green-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg text-slate-900 font-semibold">
                        Total Investment Required
                      </h3>
                      <p className="text-slate-600 text-xs mt-0.5">
                        All-inclusive price with installation & taxes
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600 font-mono">
                        {formatCurrency(subTotal!)}
                      </p>
                      <p className="text-slate-500 text-xs">Final Amount</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationDetailsModal;
